import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { createAxiosThunk } from 'utils/reduxUtil'
import ad from 'api/axios/foka/ad'
import dashboard from 'api/axios/foka/dashboard'
import { linkedTracks } from 'api/axios/crux/bcon'
import { persistFilterHistory } from 'features/dashboard/utils/storage/filterStorage'
import { current } from 'immer'
import { filterToParam } from 'features/dashboard/utils/dashboardUtils'
import { FilterMenus } from './FilterSpecs'

const initialState = {
  filters: [],
  savedFilters: [],
  isLoading: false,
  bizChannels: [],
  trackerRight: [],
  hasTrackerError: false,
}

export const fetchFilterResources = createAsyncThunk(
  'dashboard/filter/fetchFilterResources',
  async (noArgs, { getState }) => {
    const adAccountId = getState().account.current.id
    const [bizChannels, trackerRight, savedFilters] = await Promise.all([
      ad
        .getBizChannels()
        .then(res => res?.data?.content || [])
        .catch(() => []),
      linkedTracks({
        adAccountId,
        page: 0,
        size: 100,
      })
        .then(res => res?.data?.body?.content || [])
        .catch(() => []),
      dashboard
        .getSavedFilters()
        .then(res => res?.data?.contents || [])
        .catch(() => []),
    ])
    return {
      bizChannels,
      trackerRight,
      savedFilters,
    }
  }
)

export const fetchBizChannels = createAxiosThunk(
  'dashboard/filter/fetchBizChannels',
  () => ad.getBizChannels()
)

export const fetchTrackerRights = createAxiosThunk(
  'dashboard/filter/fetchTrackerRights',
  (noArgs, { getState }) => {
    const adAccountId = getState().account.current.id
    return linkedTracks({
      adAccountId,
      page: 0,
      size: 100,
    })
  }
)

export const fetchSavedFilters = createAxiosThunk(
  'dashboard/filter/fetchSavedFilters',
  () => dashboard.getSavedFilters()
)

export const saveFilter = createAxiosThunk(
  'dashboard/filter/saveFilter',
  (name, { getState }) => {
    const { filters } = getState().dashboard.filter
    return dashboard.saveFilter({
      ...filterToParam(filters),
      name,
    })
  }
)

export const deleteFilter = createAxiosThunk(
  'dashboard/filter/deleteFilter',
  async id => {
    await dashboard.deleteFilter(id)
    return dashboard.getSavedFilters()
  }
)

export const updateFilter = createAxiosThunk(
  'dashboard/filter/updateFilter',
  async (id, { getState }) => {
    const { filters, savedFilters } = getState().dashboard.filter
    const { name } = savedFilters.find(filter => filter.id === id)
    await dashboard.updateFilter({
      id,
      filter: {
        ...filterToParam(filters),
        name,
      },
    })
    return dashboard.getSavedFilters()
  }
)

const dashboardFilterSlice = createSlice({
  name: 'dashboard/filter',
  initialState,

  reducers: {
    reset: () => initialState,
    setFilters: (state, { payload }) => {
      state.filters = payload
      persistFilterHistory(payload)
    },
    addFilter: (state, { payload }) => {
      const filter =
        (payload.key === 'id' || payload.key === 'creatives') &&
        payload.data.value.endsWith(',')
          ? {
              ...payload,
              data: {
                ...payload.data,
                value: payload.data.value.slice(0, -1),
              },
            }
          : {
              ...payload,
              data: {
                ...payload.data,
                ...(payload.data.value && { value: payload.data.value.trim() }),
              },
            }
      const index = state.filters.findIndex(
        ({ domain, key }) => domain === filter.domain && key === filter.key
      )
      if (index !== -1) {
        state.filters[index] = filter
      } else {
        state.filters.push(filter)
      }
      const copy = current(state.filters)
      persistFilterHistory(copy)
    },
    removeFilter: (state, { payload }) => {
      const index = state.filters.findIndex(
        ({ domain, key }) => domain === payload.domain && key === payload.key
      )
      if (index !== -1) state.filters.splice(index, 1)
      const copy = current(state.filters)
      persistFilterHistory(copy)
    },
    clearFilters: state => {
      state.filters = []
    },
    setCampaignId: (state, { payload }) => {
      const filters = [
        {
          domain: 'campaign',
          key: 'id',
          data: {
            value: payload,
          },
        },
      ]
      state.filters = filters
      persistFilterHistory(filters)
    },
    setAdGroupId: (state, { payload }) => {
      const filters = [
        {
          domain: 'adGroup',
          key: 'id',
          data: {
            value: payload,
          },
        },
      ]
      state.filters = filters
      persistFilterHistory(filters)
    },
    setKeywordId: (state, { payload }) => {
      const filters = [
        {
          domain: 'keyword',
          key: 'id',
          data: {
            value: payload,
          },
        },
      ]
      state.filters = filters
      persistFilterHistory(filters)
    },
    setCreativeLinkId: (state, { payload }) => {
      const filters = [
        {
          domain: 'creativeLink',
          key: 'id',
          data: {
            value: payload,
          },
        },
      ]
      state.filters = filters
      persistFilterHistory(filters)
    },
    setCampaignTypeFilter: (state, { payload: campaignType }) => {
      const { domain } = FilterMenus.campaign
      const key = 'type'
      const campaignTypeFilter = {
        domain,
        key,
        data: {
          values: [campaignType],
        },
      }
      const index = state.filters.findIndex(
        filter => filter.domain === domain && filter.key === key
      )
      if (index !== -1) {
        state.filters[index] = campaignTypeFilter
      } else {
        state.filters.push(campaignTypeFilter)
      }
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchFilterResources.pending, state => {
      state.isLoading = true
    })

    builder.addCase(
      fetchFilterResources.fulfilled,
      (state, { payload: { bizChannels, trackerRight, savedFilters } }) => {
        state.bizChannels = bizChannels.map(({ id, name, siteUrl }) => ({
          id,
          name,
          siteUrl,
        }))
        state.trackerRight = trackerRight.map(({ trackId, trackName }) => ({
          id: trackId,
          name: trackName,
        }))
        state.savedFilters = savedFilters
        state.isLoading = false
      }
    )

    builder.addCase(fetchFilterResources.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(fetchBizChannels.pending, state => {
      state.isLoading = true
    })

    builder.addCase(
      fetchBizChannels.fulfilled,
      (state, { payload: { content } }) => {
        state.bizChannels = content.map(({ id, name, siteUrl }) => ({
          id,
          name,
          siteUrl,
        }))
        state.isLoading = false
      }
    )

    builder.addCase(fetchBizChannels.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(fetchTrackerRights.pending, state => {
      state.isLoading = true
    })

    builder.addCase(
      fetchTrackerRights.fulfilled,
      (
        state,
        {
          payload: {
            body: { content },
          },
        }
      ) => {
        state.trackerRight = content.map(({ trackId, trackName }) => ({
          id: trackId,
          name: trackName,
        }))
        state.isLoading = false
        state.hasTrackerError = false
      }
    )

    builder.addCase(fetchTrackerRights.rejected, state => {
      state.isLoading = false
      state.hasTrackerError = true
    })

    builder.addCase(
      fetchSavedFilters.fulfilled,
      (state, { payload: { contents } }) => {
        state.savedFilters = contents
      }
    )

    builder.addCase(saveFilter.pending, state => {
      state.isLoading = true
    })

    builder.addCase(saveFilter.fulfilled, state => {
      state.isLoading = false
    })

    builder.addCase(saveFilter.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(deleteFilter.pending, state => {
      state.isLoading = true
    })

    builder.addCase(
      deleteFilter.fulfilled,
      (state, { payload: { contents } }) => {
        state.savedFilters = contents
        state.isLoading = false
      }
    )

    builder.addCase(deleteFilter.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(updateFilter.pending, state => {
      state.isLoading = true
    })

    builder.addCase(
      updateFilter.fulfilled,
      (state, { payload: { contents } }) => {
        state.savedFilters = contents
        state.isLoading = false
      }
    )

    builder.addCase(updateFilter.rejected, state => {
      state.isLoading = false
    })
  },
})
export const {
  reset,
  setFilters,
  addFilter,
  removeFilter,
  clearFilters,
  setCampaignId,
  setAdGroupId,
  setKeywordId,
  setCreativeLinkId,
  setCampaignTypeFilter,
} = dashboardFilterSlice.actions
export default dashboardFilterSlice.reducer
